import { Button } from '@/comps/primitives/button';
import React from 'react';
import { WithClassName } from '@/types/component';
import { cn } from '@/utils/shad';
import { SwitcherItem } from '@/comps/ui/buttons/UISwitcher/types';
import { QuestionMarkToolTip } from '@/comps/tooltip-button';

type Props<TValue> = WithClassName & {
  value: TValue;
  items: SwitcherItem<TValue>[];
  onChange: (value: TValue) => void;
  disabled?: boolean;
  classNames?: {
    button?: string;
  };
  iconSize?: number;
};

export function UISwitcher<TValue>({
  className,
  items,
  value,
  onChange,
  disabled,
  classNames,
  iconSize,
}: Props<TValue>) {
  return (
    <div
      className={cn('flex h-8 items-center justify-between gap-1 rounded-full border p-1', className)}
    >
      {items.map((item, index) => {
        const hasCustomColors = !!item.colors;

        return (
          <Button
            key={index}
            variant="pill"
            className={cn(
              'h-6 w-full gap-1',
              classNames?.button,
              value === item.value ? 'bg-accent text-accent-foreground' : 'text-accent-foreground/50',
              hasCustomColors
                ? 'hover:bg-(--switcher-bg) hover:text-(--switcher-text)'
                : 'hover:bg-accent hover:text-accent-foreground',
            )}
            style={
              {
                '--switcher-bg': item.colors?.bgColor,
                '--switcher-text': item.colors?.textColor,
                backgroundColor: value === item.value ? item.colors?.bgColor : undefined,
                color: value === item.value ? item.colors?.textColor : undefined,
              } as React.CSSProperties
            }
            disabled={disabled || item.disabled}
            disabledMessage={item.disabled ? item.disabledMessage : undefined}
            {...(item.dataTour ? ({ 'data-tour': item.dataTour } as const) : {})}
            {...(item.dataTestId ? ({ 'data-testid': item.dataTestId } as const) : {})}
            onClick={() => {
              if (disabled || item.disabled) return;
              if (item.value === value) return;

              onChange(item.value);
            }}
            PrependIcon={item.icon}
            iconSize={iconSize}
          >
            {item.label}
            {item.tooltip && <QuestionMarkToolTip className="h-3 w-3" content={item.tooltip} />}
          </Button>
        );
      })}
    </div>
  );
}
